import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DashboardLayout } from '../common/page_component/index.js';
import Loader from '../common/Loader';
import Alert from '../common/Alert.js';
import routeService from '../../services/RouteService.js';
import { locationHubService } from '../../services/locationHubService.js';
import { Route as RouteIcon, ArrowLeft } from 'lucide-react';
import RouteForm, { emptyRouteForm, routeToForm, toRoutePayload } from './RouteForm.js';

const EditRoute = () => {
  const { routeId } = useParams();
  const navigate = useNavigate();
  const [hubs, setHubs] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(emptyRouteForm());
  const [lookupHubs, setLookupHubs] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        setDataLoading(true);
        const [hubsRes, routeRes] = await Promise.all([
          locationHubService.getAllHubs(1, 1000, { all: 'true' }),
          routeService.getById(routeId),
        ]);
        const list = hubsRes.data?.hubs || hubsRes.data?.data || hubsRes.data || [];
        setHubs(Array.isArray(list) ? list : []);
        const route = routeRes.data || routeRes;
        setFormData(routeToForm(route));
        setLookupHubs([
          ...(route?.source_locations || []),
          ...(route?.target_locations || []),
          ...(route?.via_locations || []),
        ]);
      } catch (err) {
        Alert.error('Failed to load route');
        console.error(err);
        navigate('/routes');
      } finally {
        setDataLoading(false);
      }
    };
    load();
  }, [routeId, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.service_number.trim()) {
      Alert.error('Service number is required');
      return;
    }
    if (!formData.source_location_ids.length || !formData.target_location_ids.length) {
      Alert.error('Source and target locations are required');
      return;
    }
    const missingBranches = formData.crossing_points.some((point) => !(point.branch_ids || []).length);
    if (missingBranches) {
      Alert.error('Each crossing location needs at least one branch');
      return;
    }

    setLoading(true);
    try {
      const response = await routeService.update(routeId, toRoutePayload(formData));
      const nextId = response.data?.route_id || formData.service_number.trim();
      Alert.success('Route updated successfully!');
      setTimeout(() => navigate(`/routes/view/${nextId}`), 1200);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to update route');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="w-full min-w-0 max-w-full mx-auto">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
          <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
            <RouteIcon className="w-6 h-6 text-secondary shrink-0" />Edit Route
          </h1>
          <button
            onClick={() => navigate('/routes')}
            className="group flex items-center justify-center gap-2 px-3 sm:px-3 py-2.5 sm:py-2 text-xs sm:text-sm text-buttonBg bg-buttonTxt hover:text-buttonTxt hover:bg-buttonBg border border-current rounded-xl transition-all duration-300 font-medium whitespace-nowrap shrink-0"
          >
            <span className="group-hover:-translate-x-1 transition-transform"><ArrowLeft className="w-4 h-4" /></span>
            Back to Routes
          </button>
        </div>

        {dataLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader fullScreen={false} size="default" message="Loading route..." />
          </div>
        ) : (
          <RouteForm
            hubs={hubs}
            lookupHubs={lookupHubs}
            formData={formData}
            setFormData={setFormData}
            onSubmit={handleSubmit}
            onCancel={() => navigate(`/routes/view/${routeId}`)}
            loading={loading}
            submitLabel="Save Route"
          />
        )}
      </div>
    </DashboardLayout>
  );
};

export default EditRoute;
